import { createSlice, createAsyncThunk, current } from "@reduxjs/toolkit";
import axios from "@/features/axiosHelper";

const settingsSlice = createSlice({
  name: "settings",
  initialState: {
    connectionProfile: { Options: [] },
    globalProfile: { Options: [] },
    connectionProfileStatus: "idle",
    globalProfileStatus: "idle",
    connectionPairStatus: "idle",
    workflowStatus: "idle",
    error: null,
    connectionPairDetails: [],
    selectedConnectionPairs: [],
    isWorkflowSettingsOpen: false,
  },
  reducers: {
    setConnectionProfile: (state, action) => {
      const { connectionProfile } = action.payload;
      state.connectionProfile = connectionProfile;
    },
    updateOption: (state, action) => {
      const { option, isGlobal } = action.payload;
      let draftState = JSON.parse(JSON.stringify(current(state)));
      let optionRef = -1;

      //Match option
      if (!isGlobal) {
        optionRef = draftState.connectionProfile.Options.findIndex(
          (x) => x.OptionId === option.OptionId
        );
      } else {
        optionRef = draftState.globalProfile.Options.findIndex(
          (x) => x.OptionId === option.OptionId
        );
      }

      if (optionRef > -1) {
        //merge options
        if (!isGlobal) {
          draftState.connectionProfile.Options[optionRef] = option;
          state.connectionProfile = draftState.connectionProfile;
        } else {
          draftState.globalProfile.Options[optionRef] = option;
          state.globalProfile = draftState.globalProfile;
        }
      }
    },
    setSelectedConnectionPairs: (state, action) => {
      const { selectedConnectionPairs } = action.payload;
      state.selectedConnectionPairs = selectedConnectionPairs;
    },
    setIsWorkflowSettingsOpen: (state, action) => {
      const { isOpen } = action.payload;
      state.isWorkflowSettingsOpen = isOpen;
    },
    updateSchedulerSettings: (state, action) => {
      const { section, newValue, isGlobal } = action.payload;

      let draftState = JSON.parse(JSON.stringify(current(state)));
      if (!isGlobal) {
        draftState.connectionProfile.WorkflowSchedule[section] = newValue;
        state.connectionProfile = draftState.connectionProfile;
      } else {
        draftState.globalProfile.WorkflowSchedule[section] = newValue;
        state.globalProfile = draftState.globalProfile;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConnectionProfile.pending, (state, action) => {
        state.connectionProfileStatus = "loading";
      })
      .addCase(fetchConnectionProfile.rejected, (state, action) => {
        state.connectionProfileStatus = "idle - failed";
        state.error = action.error.message;
      })
      .addCase(fetchConnectionProfile.fulfilled, (state, action) => {
        state.connectionProfileStatus = "idle - loaded";
        state.connectionProfile = action.payload;
      })
      .addCase(fetchGlobalProfile.pending, (state, action) => {
        state.globalProfileStatus = "loading";
      })
      .addCase(fetchGlobalProfile.rejected, (state, action) => {
        state.globalProfileStatus = "idle - failed";
        state.error = action.error.message;
      })
      .addCase(fetchGlobalProfile.fulfilled, (state, action) => {
        state.globalProfileStatus = "idle - loaded";
        state.globalProfile = action.payload;
      })
      .addCase(fetchConnectionPairs.pending, (state, action) => {
        state.connectionPairStatus = "loading";
      })
      .addCase(fetchConnectionPairs.rejected, (state, action) => {
        state.connectionPairStatus = "idle - failed";
        state.error = action.error.message;
      })
      .addCase(fetchConnectionPairs.fulfilled, (state, action) => {
        state.connectionPairStatus = "idle - loaded";
        state.connectionPairDetails = action.payload;
      })
      .addCase(fetchSingleConnectionPair.pending, (state, action) => {
        state.connectionPairStatus = "loading";
      })
      .addCase(fetchSingleConnectionPair.rejected, (state, action) => {
        state.connectionPairStatus = "idle - failed";
        state.error = action.error.message;
      })
      .addCase(fetchSingleConnectionPair.fulfilled, (state, action) => {
        state.connectionPairStatus = "idle - loaded";
      })
      .addCase(setWorkflowSchedulerSettings.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(setWorkflowSchedulerSettings.fulfilled, (state, action) => {
        const { isGlobal, newProfile } = action.payload;
        if (isGlobal) state.globalProfile = newProfile;
        else state.connectionProfile = newProfile;
      });
  },
});

export const fetchConnectionProfile = createAsyncThunk(
  "settings/fetchConnectionProfile",
  async (params) => {
    const { operationId, connectionPairId } = params;
    const response = await axios.get("/operations/connection-profiles", {
      params: {
        operationId: operationId,
        connectionPairId: connectionPairId,
      },
    });
    return response.data;
  }
);

export const fetchGlobalProfile = createAsyncThunk(
  "settings/fetchGlobalProfile",
  async (params) => {
    const { operationId } = params;
    const response = await axios.get("/operations/global-profiles", {
      params: {
        operationId: operationId,
      },
    });
    return response.data;
  }
);

export const fetchConnectionPairs = createAsyncThunk(
  "settings/fetchConnectionPairs",
  async (params, { getState }) => {
    const state = getState();
    const response = await axios.get("/connectionpair", {
      params: {
        clearCache: false,
      },
    });
    if (response?.data) {
      const data = response.data;
      const selectedConnectionPairs =
        state.tempInfo.settings.selectedConnectionPairs;
      let connPairInfo = data.map((connPair) => {
        if (selectedConnectionPairs.length > 0) {
          const ids = selectedConnectionPairs.map((x) => x.connectionPairId);
          if (ids.filter((x) => x === connPair.connectionPairId).length > 0) {
            return { ...connPair, isSelected: true };
          }
        }
        return { ...connPair, isSelected: false };
      }); // SHEP-138: Add bool flag to track selection
      return connPairInfo;
    }
    return response.data;
  }
);

export const fetchSingleConnectionPair = createAsyncThunk(
  "settings/fetchSingleConnectionPair",
  async (params) => {
    const { sourceConnectionId, targetConnectionId } = params;
    const response = await axios.get("/connectionpair/connectionpair-match", {
      params: {
        sourceConnectionId,
        targetConnectionId,
      },
    });
    return response.data;
  }
);

export const setWorkflowSchedulerSettings = createAsyncThunk(
  "settings/setWorkflowSchedulerSettings",
  async (params, { getState, rejectWithValue }) => {
    const state = getState();
    const {
      operationId,
      connectionPairId,
      isGlobal,
      newWorkflowSchedule,
      timeZone,
    } = params;

    const response = await axios
      .put(`/operations/connection-profiles/${operationId}/scheduler`, null, {
        params: {
          operationId: operationId,
          connectionPairId: connectionPairId ?? -1,
          isGlobal: isGlobal,
          objectListJson: JSON.stringify({
            ...newWorkflowSchedule,
            TimeZoneInfo: null, //404 error with nested objects, overwritten in backend so unnecessary here
          }),
          timeZone: timeZone,
        },
      })
      .then(() => {
        const newProfile = isGlobal
          ? {
              ...state.tempInfo.settings.globalProfile,
              WorkflowSchedule: newWorkflowSchedule,
            }
          : {
              ...state.tempInfo.settings.connectionProfile,
              WorkflowSchedule: newWorkflowSchedule,
            };
        return { isGlobal, newProfile };
      })
      .catch((err) => {
        return rejectWithValue(err);
      });
    return response;
  }
);

export const {
  setConnectionProfile,
  updateOption,
  setSelectedConnectionPairs,
  setIsWorkflowSettingsOpen,
  updateSchedulerSettings,
} = settingsSlice.actions;

export const selectConnectionProfile = (state) =>
  state.tempInfo.settings.connectionProfile;
export const selectGlobalProfile = (state) =>
  state.tempInfo.settings.globalProfile;
export const getConnectionProfileStatus = (state) =>
  state.tempInfo.settings.connectionProfileStatus;
export const getGlobalProfileStatus = (state) =>
  state.tempInfo.settings.globalProfileStatus;
export const selectConnectionPairDetails = (state) =>
  state.tempInfo.settings.connectionPairDetails;
export const selectedConnectionPairs = (state) =>
  state.tempInfo.settings.selectedConnectionPairs;
export const selectIsWorkflowSettingsOpen = (state) =>
  state.tempInfo.settings.isWorkflowSettingsOpen;

export default settingsSlice.reducer;
